import { createSlice } from "@reduxjs/toolkit";
import { isSilentRequestAbortError } from "../../utils/apiErrorMessage";
import { fetchSystemList, getSystemListTypeKey } from "../Thunks/systemListsThunk";

const initialState = {
  /** @type {Record<string, { list: unknown[]; status: string; error: string | null }>} */
  byListType: {},
};

export const systemListsSlice = createSlice({
  name: "systemLists",
  initialState,
  reducers: {
    clearSystemLists: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSystemList.pending, (state, action) => {
        const key = getSystemListTypeKey(action.meta.arg);
        const force = typeof action.meta.arg === "object" && action.meta.arg?.force === true;
        // Thunk may return cached data without a network call — avoid clearing "succeeded" before the thunk runs.
        if (!force && state.byListType[key]?.status === "succeeded") {
          return;
        }
        if (!state.byListType[key]) {
          state.byListType[key] = { list: [], status: "loading", error: null };
        } else {
          state.byListType[key].status = "loading";
          state.byListType[key].error = null;
        }
      })
      .addCase(fetchSystemList.fulfilled, (state, action) => {
        const key = getSystemListTypeKey(action.meta.arg);
        state.byListType[key] = {
          list: Array.isArray(action.payload) ? action.payload : [],
          status: "succeeded",
          error: null,
        };
      })
      .addCase(fetchSystemList.rejected, (state, action) => {
        const key = getSystemListTypeKey(action.meta.arg);
        const msg = action.payload ?? action.error?.message ?? "Failed to fetch system list";
        // Deduped GETs abort the prior request — do not surface as a failed state (avoids UI blink).
        if (
          isSilentRequestAbortError(action.payload) ||
          isSilentRequestAbortError(action.error) ||
          isSilentRequestAbortError(msg)
        ) {
          return;
        }
        if (!state.byListType[key]) {
          state.byListType[key] = { list: [], status: "failed", error: msg };
        } else {
          state.byListType[key].status = "failed";
          state.byListType[key].error = msg;
        }
      });
  },
});

export const { clearSystemLists } = systemListsSlice.actions;

export const selectSystemListStateForType = (state, listType) =>
  state.systemLists?.byListType?.[listType] ?? null;

export const selectSystemListItems = (state, listType) =>
  state.systemLists?.byListType?.[listType]?.list ?? null;

export default systemListsSlice.reducer;
